/**
 * Eksportet e raportit të vërejtjeve — Excel, PDF, CSV dhe printim.
 *
 * Të katërta ushqehen nga i njëjti përshkrim (`warningsLayout`), ndaj ajo që
 * sheh në ekran, ajo që shtypet dhe ajo që dërgohet janë e njëjta listë me të
 * njëjtat kolona. Më parë secili eksport i mbante kolonat e veta: CSV-ja dhe
 * "Excel"-i kishin gjashtë, PDF-ja pesë — "Lëshuar nga" thjesht binte, dhe
 * askush s'e dinte pse.
 *
 * "Excel"-i i mëparshëm ishte një tabelë HTML e ruajtur me prapashtesën `.xls`
 * dhe me MIME `application/vnd.ms-excel`. Excel-i e hap me paralajmërimin
 * "formati nuk përputhet me prapashtesën", pa ngjyra, pa gjerësi kolonash dhe
 * me thekset e prishura. Këtu shkruhet `.xlsx` i vërtetë.
 */
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SEVERITY_LEVELS, severityMeta } from '../config/warnings';
import { drawCompanyLogo } from './payrollRegisterExport';

const HEX   = { ink: '111827', muted: '6B7280', rule: 'E2E8F0', brand: '4893A5', navy: '303D50', soft: 'F8FAFC', zebra: 'FCFCFD' };
const INK   = [17, 24, 39];
const MUTED = [107, 114, 128];
const RULE  = [226, 232, 240];
const BRAND = [72, 147, 165];
const NAVY  = [48, 61, 80];

/** Data si `dd.MM.yyyy` pa kaluar nga `new Date()` për vlerat `yyyy-MM-dd`. */
export function dateText(v) {
  if (!v) return '';
  const s = String(v);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('sq-AL');
}

export const fullName = (r) => `${r.FirstName || ''} ${r.LastName || ''}`.trim() || '—';

/** Kolonat — një rend i vetëm, i lexuar nga të katër eksportet. */
export const WARNING_COLUMNS = [
  { key: 'no',    label: 'Nr.',          w:  5, align: 'center' },
  { key: 'name',  label: 'Punëtori',     w: 26 },
  { key: 'dept',  label: 'Departamenti', w: 20 },
  { key: 'subj',  label: 'Subjekti',     w: 26 },
  { key: 'sev',   label: 'Niveli',       w: 16 },
  { key: 'desc',  label: 'Përshkrimi',   w: 46, wrap: true },
  { key: 'date',  label: 'Data',         w: 12, align: 'center' },
  { key: 'by',    label: 'Lëshuar nga',  w: 18 },
];

const cellOf = (r, i) => ({
  no:   i + 1,
  name: fullName(r),
  dept: r.DepartmentName && r.DepartmentName !== '—' ? r.DepartmentName : '',
  subj: r.Subject && r.Subject !== '—' ? r.Subject : '',
  sev:  r.Severity || '',
  desc: String(r.Description || '').replace(/\s*\n\s*/g, ' ').trim(),
  date: dateText(r.CreatedAt),
  by:   r.IssuedBy || '',
});

/**
 * Përshkrimi i vetëm i faqosjes: kokë, rreshta, përmbledhje, fund.
 *
 * `filters` shkruhet në kokë me qëllim — një raport i filtruar që nuk e thotë
 * se është i filtruar lexohet si i plotë, dhe numrat e tij nuk përputhen me
 * askënd tjetër.
 */
export function warningsLayout(rows, { company = {}, filters = [], generatedAt = '' } = {}) {
  const counts = SEVERITY_LEVELS.map((s) => ({ level: s, n: rows.filter((r) => r.Severity === s).length }));
  return {
    title: 'RAPORTI I VËREJTJEVE',
    logo: company.Logo || null,
    left: [
      company.CompanyName || company.name || '—',
      company.NUI ? `NUI: ${company.NUI}` : '',
      company.Address || '',
      company.Phone || '',
    ],
    right: [
      generatedAt ? `Gjeneruar: ${generatedAt}` : '',
      `Gjithsej: ${rows.length}`,
      ...counts.map((c) => `${c.level}: ${c.n}`),
    ].filter(Boolean),
    filterLine: filters.length ? `Filtrat: ${filters.join(' · ')}` : 'Pa filtra — të gjitha vërejtjet.',
    columns: WARNING_COLUMNS,
    body: rows.map((r, i) => cellOf(r, i)),
    counts,
    slug: (generatedAt || '').replace(/[^\d]+/g, '-').replace(/^-|-$/g, '') || 'lista',
    footer: 'Dokument i brendshëm HR — përmban të dhëna personale. Trajtohet sipas politikës së mbrojtjes së të dhënave.',
  };
}

export const fileBase = (L) => `raporti-i-verejtjeve-${L.slug}`;

// ── Excel ───────────────────────────────────────────────────────────────────
export function buildWarningsWorkbook(L) {
  const cols = L.columns;
  const N = cols.length;
  const merges = [];
  const M = (r, c1, c2) => merges.push({ s: { r, c: c1 }, e: { r, c: c2 } });
  const blank = () => Array(N).fill('');
  const aoa = [];
  const RIGHT_FROM = Math.max(1, N - 3);

  const title = blank(); title[0] = L.title; aoa.push(title);
  M(0, 0, N - 1);

  for (let i = 0; i < 4; i++) {
    const row = blank();
    row[0] = L.left[i] || '';
    row[RIGHT_FROM] = L.right[i] || '';
    aoa.push(row);
    M(aoa.length - 1, 0, RIGHT_FROM - 1);
    M(aoa.length - 1, RIGHT_FROM, N - 1);
  }

  const f = blank(); f[0] = L.filterLine; aoa.push(f);
  const FILTER_ROW = aoa.length - 1;
  M(FILTER_ROW, 0, N - 1);

  aoa.push(blank());
  const RULE_ROW = aoa.length - 1;

  aoa.push(cols.map((c) => c.label));
  const HEAD_ROW = aoa.length - 1;

  L.body.forEach((b) => aoa.push(cols.map((c) => b[c.key])));
  const LAST_BODY = aoa.length - 1;

  aoa.push(blank());
  const foot = blank(); foot[0] = L.footer; aoa.push(foot);
  const FOOT_ROW = aoa.length - 1;
  M(FOOT_ROW, 0, N - 1);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((c) => ({ wch: c.w }));
  ws['!merges'] = merges;
  ws['!rows'] = [{ hpt: 22 }];

  const at = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
  const style = (r, c, st) => { const cell = at(r, c); if (cell) cell.s = { ...(cell.s || {}), ...st }; };
  const styleRow = (r, st) => { for (let c = 0; c < N; c++) style(r, c, st); };
  const line = (color, side = 'bottom', w = 'thin') => ({ [side]: { style: w, color: { rgb: color } } });

  styleRow(0, { font: { bold: true, sz: 14, color: { rgb: HEX.navy } }, alignment: { vertical: 'center' } });
  for (let r = 1; r <= 4; r++) {
    style(r, 0, { font: { sz: 9, bold: r === 1, color: { rgb: r === 1 ? HEX.ink : HEX.muted } } });
    style(r, RIGHT_FROM, { font: { sz: 9, color: { rgb: HEX.muted } }, alignment: { horizontal: 'right' } });
  }
  style(FILTER_ROW, 0, { font: { sz: 8, italic: true, color: { rgb: HEX.muted } } });
  styleRow(RULE_ROW, { border: line(HEX.brand, 'bottom', 'medium') });

  styleRow(HEAD_ROW, {
    font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
    fill: { fgColor: { rgb: HEX.soft } },
    border: line(HEX.rule),
    alignment: { horizontal: 'center', wrapText: true },
  });

  for (let r = HEAD_ROW + 1; r <= LAST_BODY; r++) {
    const zebra = (r - HEAD_ROW - 1) % 2 === 1;
    cols.forEach((col, c) => {
      // Niveli e mban ngjyrën e vet edhe në Excel: pa të, kolona më e rëndësishme
      // e raportit bëhet tekst i zakonshëm mes tekstesh të zakonshme.
      const sevCell = col.key === 'sev';
      const tone = sevCell ? severityMeta(at(r, c)?.v).color.replace('#', '') : HEX.ink;
      style(r, c, {
        font: { sz: 9, bold: sevCell, color: { rgb: tone } },
        alignment: { horizontal: col.align || 'left', wrapText: !!col.wrap, vertical: 'top' },
        border: line('F1F5F9'),
        ...(zebra ? { fill: { fgColor: { rgb: HEX.zebra } } } : {}),
      });
    });
  }

  style(FOOT_ROW, 0, { font: { sz: 8, color: { rgb: HEX.muted } } });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Vërejtjet');
  return wb;
}

export function exportWarningsExcel(L) {
  XLSX.writeFile(buildWarningsWorkbook(L), `${fileBase(L)}.xlsx`);
}

// ── PDF ─────────────────────────────────────────────────────────────────────
export function buildWarningsPdf(L) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 10;

  // Logoja është e KOMPANISË, jo e Nexuma-s: raportin e nënshkruan punëdhënësi.
  const logoW = drawCompanyLogo(doc, L.logo, M, 9.5, 10, 36);
  const titleX = M + (logoW ? logoW + 4 : 0);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...NAVY);
  doc.text(L.title, titleX, 16.5);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...INK);
  doc.text(L.left[0] || '—', M, 24);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...MUTED);
  L.left.slice(1).filter(Boolean).forEach((l, i) => doc.text(l, M, 28.5 + i * 3.6));
  L.right.forEach((l, i) => doc.text(l, PW - M, 14 + i * 3.6, { align: 'right' }));
  doc.setFont('helvetica', 'italic');
  doc.text(L.filterLine, M, 38.5);

  doc.setDrawColor(...BRAND); doc.setLineWidth(0.5);
  doc.line(M, 41, PW - M, 41);

  const cols = L.columns;
  autoTable(doc, {
    head: [cols.map((c) => ({ content: c.label, styles: { halign: c.align || 'left' } }))],
    body: L.body.map((b) => cols.map((c) => String(b[c.key] ?? ''))),
    startY: 45,
    margin: { left: M, right: M, bottom: 16 },
    theme: 'plain',
    styles: { fontSize: 6.4, cellPadding: { top: 1.4, bottom: 1.4, left: 1.4, right: 1.4 }, textColor: INK, overflow: 'linebreak', valign: 'top' },
    headStyles: { fillColor: [248, 250, 252], textColor: INK, fontSize: 6, fontStyle: 'bold', lineColor: RULE, lineWidth: { bottom: 0.3 } },
    bodyStyles: { lineColor: [241, 245, 249], lineWidth: { bottom: 0.1 } },
    columnStyles: Object.fromEntries(cols.map((c, i) => [i, {
      halign: c.align || 'left',
      cellWidth: c.wrap ? 'auto' : undefined,
      textColor: c.key === 'desc' || c.key === 'by' ? MUTED : INK,
    }])),
    didParseCell: (d) => {
      // Niveli merr ngjyrën e vet — e vetmja kolonë ku ngjyra mbart kuptim.
      if (d.section === 'body' && cols[d.column.index]?.key === 'sev') {
        d.cell.styles.textColor = severityMeta(d.cell.raw).rgb;
        d.cell.styles.fontStyle = 'bold';
      }
    },
  });

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.6); doc.setTextColor(...MUTED);
    doc.text(L.footer, M, PH - 6);
    doc.text(`${p} / ${pages}`, PW - M, PH - 6, { align: 'right' });
  }
  return doc;
}

export function downloadWarningsPdf(L) { buildWarningsPdf(L).save(`${fileBase(L)}.pdf`); }
export function printWarnings(L) {
  // Një dokument i vetëm: `autoPrint` shkruan një veprim BRENDA PDF-së, ndaj
  // ndërtimi i dytë do ta kishte hedhur poshtë pikërisht atë veprim.
  const doc = buildWarningsPdf(L);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
}

// ── CSV ─────────────────────────────────────────────────────────────────────
/**
 * BOM-i në krye nuk është zbukurim: pa të, Excel-i e hap CSV-në si Windows-1252
 * dhe çdo ë e ç del si simbol i thyer — pikërisht te emrat e punëtorëve.
 */
export function toCSV(L) {
  const esc = (x) => `"${String(x ?? '').replace(/"/g, '""')}"`;
  const lines = [L.columns.map((c) => esc(c.label)).join(',')];
  L.body.forEach((b) => lines.push(L.columns.map((c) => esc(b[c.key])).join(',')));
  return `\uFEFF${lines.join('\r\n')}`;
}

export function downloadWarningsCsv(L) {
  const blob = new Blob([toCSV(L)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${fileBase(L)}.csv`; a.click();
  URL.revokeObjectURL(url);
}
